import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { COMPUTED_KEYS, computeCell, fmtCell, sanitizeNumeric, toNum } from "@/lib/compute";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, Download, Table2, ArrowDownToLine, TriangleAlert, Plus, Trash2, CheckCircle2, Landmark, MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function shiftMonth(m, delta) {
  const [y, mo] = (m || currentMonth()).split("-").map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function dayName(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" });
}

export default function MonthlyReport() {
  const { user } = useAuth();
  const readOnly = user?.role === "owner";
  const canSave = can(user, ["admin", "regional", "gm", "finance"]);

  const [props, setProps] = useState([]);
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const [month, setMonth] = useState(currentMonth());
  const [allColumns, setAllColumns] = useState([]);
  const [dates, setDates] = useState([]);
  const [rows, setRows] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [oooDate, setOooDate] = useState(null);
  const [recon, setRecon] = useState({});
  const [reconDate, setReconDate] = useState(null);
  const [reconEdit, setReconEdit] = useState({ bank_deposit: "", comment: "" });
  const [osDate, setOsDate] = useState(null);
  const [osText, setOsText] = useState("");
  const canApprove = can(user, ["admin", "regional", "finance"]);

  const CB_STATUSES = [
    { v: "open", l: "Open" },
    { v: "under_review", l: "Under Review" },
    { v: "won", l: "Won" },
    { v: "lost", l: "Lost" },
    { v: "refunded", l: "Refunded" },
  ];
  const cbLabel = (v) => (CB_STATUSES.find((s) => s.v === v) || CB_STATUSES[0]).l;
  const [chargebacks, setChargebacks] = useState([]);
  const loadChargebacks = useCallback(() => {
    if (!propertyId || !month) return;
    api.get("/chargebacks", { params: { property_id: propertyId, month } })
      .then((r) => setChargebacks(r.data)).catch(() => setChargebacks([]));
  }, [propertyId, month]);
  useEffect(() => { loadChargebacks(); }, [loadChargebacks]);

  // Monthly history (manual prior-year figures) — surfaced when the month has no daily entries
  const [finMonth, setFinMonth] = useState(null);
  useEffect(() => {
    if (!propertyId || !month) { setFinMonth(null); return; }
    const [y, m] = month.split("-").map(Number);
    api.get("/monthly-financials", { params: { property_id: propertyId, year: y } })
      .then((r) => setFinMonth((r.data.rows || []).find((x) => x.month === m) || null))
      .catch(() => setFinMonth(null));
  }, [propertyId, month]);

  const patchChargeback = async (cid, patch) => {
    setChargebacks((cs) => cs.map((c) => (c.id === cid ? { ...c, ...patch } : c)));
    try {
      await api.patch(`/chargebacks/${cid}`, patch);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      loadChargebacks();
    }
  };
  const openRecon = (d) => {
    const r = recon[d] || {};
    setReconEdit({ bank_deposit: r.bank_deposit || "", comment: r.comment || "" });
    setReconDate(d);
  };
  const saveRecon = async (status) => {
    const payload = {
      property_id: propertyId,
      date: reconDate,
      status: status ?? (recon[reconDate]?.status || "pending"),
      bank_deposit: reconEdit.bank_deposit,
      comment: reconEdit.comment,
    };
    try {
      const res = await api.post("/reconciliation", payload);
      setRecon((prev) => ({ ...prev, [reconDate]: { ...payload, approved_by: res.data.approved_by, approved_at: res.data.approved_at } }));
      toast.success(status === "approved" ? "Approved & reconciled" : status === "pending" ? "Set back to pending" : "Reconciliation saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const prop = useMemo(() => props.find((p) => p.id === propertyId), [props, propertyId]);
  const hasDaily = useMemo(
    () => Object.values(rows || {}).some((r) => r && ["rooms_sold", "room_revenue", "f_b_revenue", "pantry_revenue", "misc_revenue", "meeting_room"].some((k) => r[k] !== "" && r[k] != null && Number(r[k]))),
    [rows],
  );
  const finHasData = finMonth && (Number(finMonth.revenue) || Number(finMonth.adr) || Number(finMonth.occupancy) || Number(finMonth.revpar));
  const totalRooms = Number(prop?.rooms) || 0;
  const columns = useMemo(() => {
    const hidden = new Set(prop?.hidden_columns || []);
    return allColumns.filter((c) => !hidden.has(c.key));
  }, [allColumns, prop]);

  const editableCols = useMemo(
    () => columns.map((c, i) => (COMPUTED_KEYS.has(c.key) ? null : i)).filter((i) => i !== null),
    [columns]
  );

  const focusCell = (ri, ci) => {
    const el = document.querySelector(`[data-nav="${ri}-${ci}"]`);
    if (el) {
      el.focus();
      if (el.select) el.select();
    }
  };

  const onCellKeyDown = (e, ri, ci) => {
    if (e.key === "Enter") {
      e.preventDefault();
      focusCell(e.shiftKey ? ri - 1 : ri + 1, ci);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      focusCell(ri + 1, ci);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusCell(ri - 1, ci);
    } else if (e.key === "Tab") {
      // let native tab move within the row; wrap to next row's first editable cell at row end
      const pos = editableCols.indexOf(ci);
      if (!e.shiftKey && pos === editableCols.length - 1) {
        e.preventDefault();
        focusCell(ri + 1, editableCols[0]);
      } else if (e.shiftKey && pos === 0) {
        e.preventDefault();
        focusCell(ri - 1, editableCols[editableCols.length - 1]);
      }
    }
  };

  const onCellPaste = (e, ri, ci) => {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    if (!text.includes("\t") && !text.includes("\n")) {
      // single value: sanitize numeric columns so commas/$ don't get stored raw
      const col = columns[ci];
      if (col && !COMPUTED_KEYS.has(col.key) && col.type !== "text" && col.type !== "notes" && col.type !== "checkbox") {
        e.preventDefault();
        const d = dates[ri];
        if (d) setCell(d, col.key, sanitizeNumeric(text));
      }
      return; // let non-numeric single values paste normally
    }
    e.preventDefault();
    const grid = text
      .replace(/\r/g, "")
      .split("\n")
      .filter((l, i, arr) => !(i === arr.length - 1 && l === ""))
      .map((l) => l.split("\t"));
    let written = 0;
    setRows((prev) => {
      const next = { ...prev };
      grid.forEach((line, r) => {
        const d = dates[ri + r];
        if (!d) return;
        const rowObj = { ...(next[d] || {}) };
        line.forEach((val, c) => {
          const col = columns[ci + c];
          if (!col || COMPUTED_KEYS.has(col.key)) return; // skip computed columns (auto-calculated)
          rowObj[col.key] = (col.type === "text" || col.type === "notes") ? val.trim()
            : col.type === "checkbox" ? ["1", "true", "yes", "y", "x", "✓"].includes(val.trim().toLowerCase())
            : sanitizeNumeric(val);
          written += 1;
        });
        next[d] = rowObj;
      });
      return next;
    });
    toast.success(`Pasted ${grid.length} row${grid.length > 1 ? "s" : ""}`);
  };

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
    });
    api.get("/grid-config").then((r) => setAllColumns(r.data.columns));
  }, []);

  const loadData = useCallback(() => {
    if (!propertyId || !month) return;
    setLoading(true);
    api
      .get("/monthly-report", { params: { property_id: propertyId, month } })
      .then((r) => {
        setDates(r.data.dates);
        setRows(r.data.rows || {});
        setDirty(new Set());
      })
      .finally(() => setLoading(false));
  }, [propertyId, month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirty.size > 0) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  useEffect(() => {
    if (!propertyId || !month) return;
    api.get("/reconciliation", { params: { property_id: propertyId, month } })
      .then((r) => setRecon(r.data || {}))
      .catch(() => setRecon({}));
  }, [propertyId, month]);

  // default Rooms Available to the property's total rooms
  useEffect(() => {
    if (!totalRooms || !dates.length) return;
    setRows((prev) => {
      const next = { ...prev };
      let changed = false;
      dates.forEach((d) => {
        const cur = next[d] || {};
        if (cur.rooms_available === undefined || cur.rooms_available === "") {
          next[d] = { ...cur, rooms_available: totalRooms };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [totalRooms, dates]);

  const setCell = (date, key, value) => {
    setRows((prev) => ({ ...prev, [date]: { ...(prev[date] || {}), [key]: value } }));
    setDirty((prev) => (prev.has(`${date}:${key}`) ? prev : new Set(prev).add(`${date}:${key}`)));
  };

  const copyPrevDay = (index) => {
    if (index <= 0) return;
    const prevRow = rows[dates[index - 1]] || {};
    const clean = {};
    Object.keys(prevRow).forEach((k) => {
      if (!COMPUTED_KEYS.has(k) && k !== "ooo_explanations") clean[k] = prevRow[k];
    });
    setRows((prev) => ({ ...prev, [dates[index]]: { ...(prev[dates[index]] || {}), ...clean } }));
    setDirty((prev) => { const n = new Set(prev); Object.keys(clean).forEach((k) => n.add(`${dates[index]}:${k}`)); return n; });
    toast.success("Copied previous day");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(rows).forEach(([d, vals]) => {
        const clean = {};
        Object.keys(vals || {}).forEach((k) => {
          if (!COMPUTED_KEYS.has(k)) clean[k] = vals[k];
        });
        payload[d] = clean;
      });
      await api.post("/monthly-report/save", { property_id: propertyId, month, rows: payload });
      setDirty(new Set());
      toast.success("Monthly report saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get("/monthly-report/export", {
        params: { property_id: propertyId, month },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(prop?.name || "report").replace(/\s+/g, "_")}_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const openOsComment = (d) => { setOsDate(d); setOsText(rows[d]?.over_short_comment || ""); };
  const saveOsComment = () => {
    setCell(osDate, "over_short_comment", osText);
    setOsDate(null);
    toast.success("Comment added — click Save to persist");
  };

  const cellValue = (date, col) => {
    if (COMPUTED_KEYS.has(col.key)) return computeCell(col.key, rows[date] || {}, totalRooms);
    return rows[date]?.[col.key] ?? "";
  };

  // totals — aggregation depends on column meaning:
  //  • average (over days that actually have data): rates/ratios
  //  • last entry (running balances / point-in-time): AR pending, guest ledger
  //  • sum: everything else (revenue, deposits, etc.)
  const AVG_KEYS = new Set(["adr", "rev_par", "occupancy_percentage", "occupancy_pct", "revpar"]);
  const LAST_KEYS = new Set(["ar_pending_till_date", "total_guest_ledger", "accounts_receivable", "open_folios"]);
  const dayHasData = (d) => {
    const rs = rows[d]?.rooms_sold;
    return rs !== "" && rs != null && !isNaN(toNum(rs));
  };
  const dataDates = dates.filter(dayHasData);

  const totals = {};
  columns.forEach((c) => {
    if (LAST_KEYS.has(c.key)) {
      let val = null;
      for (let i = dataDates.length - 1; i >= 0; i--) {
        const v = toNum(rows[dataDates[i]]?.[c.key]);
        if (!isNaN(v)) { val = v; break; }
      }
      totals[c.key] = { kind: c.type === "percent" ? "percent" : c.type === "number" ? "number" : "currency", value: val ?? 0 };
    } else if (AVG_KEYS.has(c.key)) {
      let sum = 0, cnt = 0;
      dataDates.forEach((d) => {
        const v = toNum(cellValue(d, c));
        if (!isNaN(v)) { sum += v; cnt += 1; }
      });
      totals[c.key] = { kind: c.type === "percent" ? "percent" : "currency", value: cnt ? sum / cnt : 0 };
    } else if (c.type === "currency" || c.type === "number") {
      let sum = 0;
      dates.forEach((d) => {
        const v = toNum(cellValue(d, c));
        if (!isNaN(v)) sum += v;
      });
      totals[c.key] = { kind: c.type, value: sum };
    } else if (c.type === "percent") {
      let sum = 0, cnt = 0;
      dataDates.forEach((d) => {
        const v = toNum(cellValue(d, c));
        if (!isNaN(v)) { sum += v; cnt += 1; }
      });
      totals[c.key] = { kind: "percent", value: cnt ? sum / cnt : 0 };
    }
  });

  const oooRow = oooDate ? rows[oooDate] || {} : {};
  const oooList = oooRow.ooo_explanations || [];
  const setOoo = (list) => setRows((prev) => ({ ...prev, [oooDate]: { ...(prev[oooDate] || {}), ooo_explanations: list } }));

  return (
    <div className="space-y-6" data-testid="monthly-report-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Excel-Style Reporting</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Monthly Report</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Occupancy, Hotel &amp; Total Revenue calculate automatically. Rooms Available defaults to the property size.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            <kbd className="rounded border border-border px-1">Enter</kbd> next row ·{" "}
            <kbd className="rounded border border-border px-1">Tab</kbd> next cell ·{" "}
            <kbd className="rounded border border-border px-1">↑</kbd>/<kbd className="rounded border border-border px-1">↓</kbd> move between days ·{" "}
            <kbd className="rounded border border-border px-1">Ctrl/⌘+V</kbd> paste from Excel
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="w-52" data-testid="monthly-property-select">
              <SelectValue placeholder="Select property" />
            </SelectTrigger>
            <SelectContent>
              {props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="monthly-month-prev" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></Button>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-36 text-center sm:w-40" data-testid="monthly-month-input" />
            <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="monthly-month-next" aria-label="Next month"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Button variant="outline" onClick={downloadExcel} disabled={!propertyId} data-testid="download-excel-btn">
            <Download className="mr-2 h-4 w-4" /> Excel
          </Button>
          {!readOnly && canSave && (
            <div className="flex items-center gap-2">
              {dirty.size > 0 && (
                <span className="flex items-center gap-1.5 rounded-md bg-yellow-200/70 px-2.5 py-1 text-xs font-semibold text-yellow-900 dark:bg-yellow-500/25 dark:text-yellow-200" data-testid="unsaved-indicator">
                  <span className="h-2 w-2 rounded-full bg-yellow-500" /> {dirty.size} unsaved
                </span>
              )}
              <Button onClick={save} disabled={saving || !propertyId} data-testid="save-monthly-btn"
                className={dirty.size > 0 ? "bg-yellow-500 text-black hover:bg-yellow-600" : ""}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save{dirty.size > 0 ? " *" : ""}
              </Button>
            </div>
          )}
        </div>
      </div>

      {propertyId && !hasDaily && finHasData ? (
        <div className="border border-primary/40 bg-primary/5 p-4" data-testid="historical-summary-banner">
          <div className="label-caps text-xs text-primary">Monthly history · no daily entries for this month</div>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "Occupancy", value: `${(Number(finMonth.occupancy) || 0).toFixed(2)}%` },
              { label: "ADR", value: `$${(Number(finMonth.adr) || 0).toFixed(2)}` },
              { label: "RevPAR", value: `$${(Number(finMonth.revpar) || 0).toFixed(2)}` },
              { label: "Hotel Revenue", value: `$${(Number(finMonth.revenue) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            ].map((s) => (
              <div key={s.label} className="border border-border bg-card p-3" data-testid={`hist-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="label-caps text-[0.6rem] text-muted-foreground">{s.label}</div>
                <div className="mt-1 font-heading text-xl font-bold">{s.value}</div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Entered under Daily Report Entry → Monthly Entry (Prior Years). Enter daily rows below to replace this summary.</p>
        </div>
      ) : null}

      {!propertyId ? (
        <div className="flex h-64 flex-col items-center justify-center border border-dashed border-border text-muted-foreground">
          <Table2 className="h-8 w-8" /><p className="mt-3 text-sm">Select a property to begin.</p>
        </div>
      ) : loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="relative overflow-auto border border-border bg-card" style={{ maxHeight: "70vh" }}>
          <table className="border-collapse text-sm" data-testid="monthly-grid">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className="sticky left-0 z-30 border border-border bg-secondary px-2 py-2 text-left label-caps text-muted-foreground" style={{ minWidth: 56 }}>Date</th>
                <th className="sticky z-30 border border-border bg-secondary px-2 py-2 text-left label-caps text-muted-foreground" style={{ left: 56, minWidth: 92 }}>Day</th>
                {columns.map((c) => (
                  <th key={c.key} className="whitespace-nowrap border border-border bg-secondary px-3 py-2 text-left text-xs font-semibold" style={{ minWidth: 120 }}>
                    {c.label}
                    {COMPUTED_KEYS.has(c.key) && <span className="ml-1 text-primary" title="Calculated automatically">ƒ</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((d, ri) => {
                const isWeekend = ["Sat", "Sun"].includes(dayName(d));
                const ra = Number(rows[d]?.rooms_available ?? totalRooms);
                const hasOOO = ra < totalRooms;
                const issues = (rows[d]?.ooo_explanations || []).length;
                return (
                  <tr key={d} className={isWeekend ? "bg-accent/40" : ri % 2 ? "bg-muted/30" : ""}>
                    <td className="sticky left-0 z-10 border border-border bg-card px-2 py-1 font-mono text-xs font-medium" style={{ minWidth: 56 }}>
                      {d.slice(5, 7)}/{d.slice(8, 10)}
                    </td>
                    <td className="sticky z-10 border border-border bg-card px-1 py-1" style={{ left: 56, minWidth: 92 }}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs text-muted-foreground">{dayName(d)}</span>
                        <div className="flex items-center gap-0.5">
                          {!readOnly && ri > 0 && (
                            <button onClick={() => copyPrevDay(ri)} title="Copy previous day" data-testid={`copy-prev-${d}`} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-primary">
                              <ArrowDownToLine className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {hasOOO && (
                            <button onClick={() => setOooDate(d)} title="Room issues / remedy" data-testid={`ooo-btn-${d}`} className={`rounded p-1 ${issues ? "text-[hsl(var(--warning))]" : "text-muted-foreground"} hover:bg-secondary`}>
                              <TriangleAlert className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    {columns.map((c, ci) => {
                      const computed = COMPUTED_KEYS.has(c.key);
                      if (computed) {
                        if (c.key === "pms_total") {
                          const approved = recon[d]?.status === "approved";
                          return (
                            <td key={c.key} className="border border-border p-0" style={{ minWidth: 120 }}>
                              <button
                                onClick={() => openRecon(d)}
                                data-testid={`recon-cell-${d}`}
                                title={approved ? "Reconciled — click to view" : "Click to reconcile bank deposit"}
                                className={`flex h-8 w-full items-center justify-end gap-1 px-2 font-mono text-xs transition-colors ${
                                  approved
                                    ? "bg-emerald-500/25 font-semibold text-emerald-700 dark:text-emerald-300"
                                    : "bg-accent/30 text-accent-foreground hover:bg-primary/15"
                                }`}
                              >
                                {approved && <CheckCircle2 className="h-3 w-3 shrink-0" />}
                                {fmtCell(c.type, cellValue(d, c))}
                              </button>
                            </td>
                          );
                        }
                        return (
                          <td key={c.key} className="border border-border bg-accent/30 px-2 text-right font-mono text-xs text-accent-foreground" style={{ minWidth: 120 }} data-testid={`cell-${d}-${c.key}`}>
                            {fmtCell(c.type, cellValue(d, c))}
                          </td>
                        );
                      }
                      if (c.type === "checkbox") {
                        const isDirty = dirty.has(`${d}:${c.key}`);
                        return (
                          <td key={c.key} className={`border border-border p-0 text-center ${isDirty ? "bg-yellow-200/70 dark:bg-yellow-500/25" : ""}`} style={{ minWidth: 120 }}>
                            <div className="flex h-8 items-center justify-center">
                              <input
                                type="checkbox"
                                disabled={readOnly}
                                checked={!!rows[d]?.[c.key]}
                                onChange={(e) => setCell(d, c.key, e.target.checked)}
                                className="h-4 w-4 accent-primary"
                                data-testid={`cell-${d}-${c.key}`}
                              />
                            </div>
                          </td>
                        );
                      }
                      if (c.key === "over_short") {
                        const isDirty = dirty.has(`${d}:${c.key}`);
                        const hasComment = !!(rows[d]?.over_short_comment || "").trim();
                        return (
                          <td key={c.key} className={`border border-border p-0 ${isDirty ? "bg-yellow-200/70 dark:bg-yellow-500/25" : ""}`} style={{ minWidth: 120 }} data-dirty={isDirty ? "1" : undefined}>
                            <div className="flex h-8 items-center">
                              <input
                                type="text"
                                inputMode="decimal"
                                disabled={readOnly}
                                value={rows[d]?.[c.key] ?? ""}
                                onChange={(e) => setCell(d, c.key, sanitizeNumeric(e.target.value))}
                                onKeyDown={(e) => onCellKeyDown(e, ri, ci)}
                                onPaste={(e) => onCellPaste(e, ri, ci)}
                                data-nav={`${ri}-${ci}`}
                                data-testid={`cell-${d}-${c.key}`}
                                className="h-8 min-w-0 flex-1 border-0 bg-transparent px-2 text-right font-mono text-xs outline-none focus:bg-accent focus:ring-1 focus:ring-primary"
                              />
                              <button
                                type="button"
                                onClick={() => openOsComment(d)}
                                title={hasComment ? "View / edit over-short comment" : "Add over-short comment"}
                                data-testid={`os-comment-btn-${d}`}
                                className={`flex h-8 w-6 shrink-0 items-center justify-center ${hasComment ? "text-primary" : "text-muted-foreground/40 hover:text-primary"}`}
                              >
                                <MessageSquare className={`h-3.5 w-3.5 ${hasComment ? "fill-primary/20" : ""}`} />
                              </button>
                            </div>
                          </td>
                        );
                      }
                      const isTexty = c.type === "text" || c.type === "notes";
                      const isDirty = dirty.has(`${d}:${c.key}`);
                      return (
                        <td key={c.key} className={`border border-border p-0 ${isDirty ? "bg-yellow-200/70 dark:bg-yellow-500/25" : ""}`} style={{ minWidth: 120 }} data-dirty={isDirty ? "1" : undefined}>
                          <input
                            type="text"
                            inputMode={isTexty ? "text" : "decimal"}
                            disabled={readOnly}
                            value={rows[d]?.[c.key] ?? ""}
                            onChange={(e) => setCell(d, c.key, isTexty ? e.target.value : sanitizeNumeric(e.target.value))}
                            onKeyDown={(e) => onCellKeyDown(e, ri, ci)}
                            onPaste={(e) => onCellPaste(e, ri, ci)}
                            data-nav={`${ri}-${ci}`}
                            data-testid={`cell-${d}-${c.key}`}
                            className={`h-8 w-full border-0 bg-transparent px-2 text-xs outline-none focus:bg-accent focus:ring-1 focus:ring-primary ${isTexty ? "text-left" : "text-right font-mono"}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="sticky bottom-0 z-10 font-semibold">
                <td className="sticky left-0 z-20 border border-border bg-primary px-2 py-1.5 text-xs text-primary-foreground" style={{ minWidth: 56 }}>TOTAL</td>
                <td className="sticky z-20 border border-border bg-primary px-2 py-1.5 text-primary-foreground" style={{ left: 56, minWidth: 92 }} />
                {columns.map((c) => (
                  <td key={c.key} className="border border-border bg-primary px-2 py-1.5 text-right font-mono text-xs text-primary-foreground" style={{ minWidth: 120 }}>
                    {totals[c.key] !== undefined ? (totals[c.key].kind === "percent" ? fmtCell("percent", totals[c.key].value) : fmtCell(totals[c.key].kind, totals[c.key].value)) : ""}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {propertyId && (
        <div className="mt-8" data-testid="monthly-chargebacks">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="label-caps text-primary">Chargebacks</div>
              <h2 className="font-heading text-xl font-bold">Chargebacks this month</h2>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Total</div>
              <div className="font-heading text-2xl font-black tabular-nums" data-testid="monthly-chargebacks-total">
                ${chargebacks.reduce((s, c) => s + (Number(c.amount) || 0), 0).toFixed(2)}
              </div>
            </div>
          </div>
          {chargebacks.length === 0 ? (
            <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No chargebacks recorded this month. GMs add them at the bottom of Daily Report Entry.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-left">
                    <th className="p-2 font-semibold">Date</th>
                    <th className="p-2 text-right font-semibold">Amount</th>
                    <th className="p-2 font-semibold">Note / Guest</th>
                    <th className="p-2 font-semibold">Resolution</th>
                    <th className="p-2 font-semibold">Notes on outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {chargebacks.map((c) => (
                    <tr key={c.id} className="border-b border-border" data-testid={`monthly-cb-row-${c.id}`}>
                      <td className="whitespace-nowrap p-2 font-medium">{c.report_date}</td>
                      <td className="p-2 text-right font-mono font-semibold tabular-nums">${Number(c.amount).toFixed(2)}</td>
                      <td className="p-2 text-muted-foreground">{c.note || "—"}</td>
                      <td className="p-2">
                        {canSave ? (
                          <Select value={c.status || "open"} onValueChange={(v) => patchChargeback(c.id, { status: v })}>
                            <SelectTrigger className="h-8 w-36" data-testid={`monthly-cb-status-${c.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{CB_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : cbLabel(c.status)}
                      </td>
                      <td className="p-2">
                        {canSave ? (
                          <Input defaultValue={c.resolution_note || ""} placeholder="What happened…"
                            onBlur={(e) => e.target.value !== (c.resolution_note || "") && patchChargeback(c.id, { resolution_note: e.target.value })}
                            className="h-8 min-w-[180px]" data-testid={`monthly-cb-resolution-${c.id}`} />
                        ) : (c.resolution_note || "—")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Room issues / remedy dialog */}
      <Dialog open={!!oooDate} onOpenChange={(o) => !o && setOooDate(null)}>
        <DialogContent className="max-w-2xl" data-testid="ooo-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Rooms Not Available — {oooDate}</DialogTitle>
            <DialogDescription>
              {prop?.name}: {Number(oooRow.rooms_available ?? totalRooms)} of {totalRooms} rooms available. Explain each out-of-order room.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 label-caps text-muted-foreground">
              <div className="col-span-2">Room No</div>
              <div className="col-span-5">Issue</div>
              <div className="col-span-4">Remedy</div>
              <div className="col-span-1" />
            </div>
            {oooList.map((row, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-2" data-testid={`ooo-row-${i}`}>
                <Input className="col-span-2" value={row.room || ""} disabled={readOnly} onChange={(e) => setOoo(oooList.map((x, xi) => (xi === i ? { ...x, room: e.target.value } : x)))} />
                <Input className="col-span-5" value={row.issue || ""} disabled={readOnly} onChange={(e) => setOoo(oooList.map((x, xi) => (xi === i ? { ...x, issue: e.target.value } : x)))} />
                <Input className="col-span-4" value={row.remedy || ""} disabled={readOnly} onChange={(e) => setOoo(oooList.map((x, xi) => (xi === i ? { ...x, remedy: e.target.value } : x)))} />
                {!readOnly && (
                  <Button size="icon" variant="ghost" className="col-span-1" onClick={() => setOoo(oooList.filter((_, xi) => xi !== i))}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
            {oooList.length === 0 && <p className="py-2 text-sm text-muted-foreground">No room issues logged.</p>}
          </div>
          <DialogFooter className="sm:justify-between">
            {!readOnly && (
              <Button variant="outline" onClick={() => setOoo([...oooList, { room: "", issue: "", remedy: "" }])} data-testid="add-ooo-btn">
                <Plus className="mr-2 h-4 w-4" /> Add Room
              </Button>
            )}
            <Button onClick={() => setOooDate(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PMS Total reconciliation dialog */}
      <Dialog open={!!reconDate} onOpenChange={(o) => !o && setReconDate(null)}>
        <DialogContent className="max-w-md" data-testid="recon-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reconcile PMS Total — {reconDate}</DialogTitle>
            <DialogDescription>
              Match the system totals to the deposit received in the bank, then approve. Bank deposits often land 2–3 days later.
            </DialogDescription>
          </DialogHeader>
          {reconDate && (() => {
            const row = rows[reconDate] || {};
            const items = [
              ["PMS System Total", computeCell("pms_total", row, totalRooms)],
              ["PMS System AMEX", Number(row.pms_amex || 0)],
              ["PMS System CC Total", computeCell("pms_cc_total", row, totalRooms)],
            ];
            const r = recon[reconDate] || {};
            const approved = r.status === "approved";
            return (
              <div className="space-y-4">
                <div className="rounded-lg border border-border">
                  {items.map(([label, val], i) => (
                    <div key={label} className={`flex items-center justify-between px-3 py-2 text-sm ${i > 0 ? "border-t border-border" : ""}`} data-testid={`recon-val-${i}`}>
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-mono font-semibold">{fmtCell("currency", val)}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5" /> Bank Deposit Received</Label>
                  <Input
                    value={reconEdit.bank_deposit}
                    disabled={!canApprove}
                    onChange={(e) => setReconEdit((s) => ({ ...s, bank_deposit: sanitizeNumeric(e.target.value) }))}
                    placeholder="0.00"
                    className="font-mono"
                    data-testid="recon-bank-deposit"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Comment</Label>
                  <Textarea
                    value={reconEdit.comment}
                    disabled={!canApprove}
                    onChange={(e) => setReconEdit((s) => ({ ...s, comment: e.target.value }))}
                    placeholder="Notes about this settlement / deposit…"
                    data-testid="recon-comment"
                  />
                </div>
                <div className={`rounded-md px-3 py-2 text-sm ${approved ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`} data-testid="recon-status">
                  {approved ? <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Approved by {r.approved_by} · {r.approved_at ? new Date(r.approved_at).toLocaleDateString() : ""}</span> : "Pending approval"}
                </div>
                {!canApprove && <p className="text-xs text-muted-foreground">Only Admin, Regional and Finance can reconcile.</p>}
              </div>
            );
          })()}
          <DialogFooter className="sm:justify-between">
            {canApprove && (
              recon[reconDate]?.status === "approved" ? (
                <Button variant="outline" onClick={() => saveRecon("pending")} data-testid="recon-unapprove-btn">Unapprove</Button>
              ) : (
                <Button onClick={() => saveRecon("approved")} data-testid="recon-approve-btn"><CheckCircle2 className="mr-2 h-4 w-4" /> Approve</Button>
              )
            )}
            <div className="flex gap-2">
              {canApprove && <Button variant="outline" onClick={() => saveRecon()} data-testid="recon-save-btn"><Save className="mr-2 h-4 w-4" /> Save</Button>}
              <Button variant="ghost" onClick={() => setReconDate(null)}>Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Over/Short comment dialog */}
      <Dialog open={!!osDate} onOpenChange={(o) => !o && setOsDate(null)}>
        <DialogContent className="max-w-md" data-testid="os-comment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Over/Short comment — {osDate}</DialogTitle>
            <DialogDescription>
              Explain the Over/Short (Bank Deposit − Actual Deposit) for this day — whether it's positive or negative.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            disabled={readOnly}
            value={osText}
            onChange={(e) => setOsText(e.target.value)}
            placeholder="e.g. $20 short — till miscount, corrected next day"
            data-testid="os-comment-text"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOsDate(null)}>Close</Button>
            {!readOnly && <Button onClick={saveOsComment} data-testid="os-comment-save"><Save className="mr-2 h-4 w-4" /> Save comment</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
